import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Check, Loader2 } from "lucide-react";
import api from "../../lib/api";

const MODES = [
  { key: "premium", label: "Premium tone" },
  { key: "shorter", label: "Make shorter" },
  { key: "clarity", label: "Improve clarity" },
  { key: "grammar", label: "Fix grammar" },
  { key: "persuasive", label: "More persuasive" },
  { key: "variations", label: "3 variations" },
];

export default function AIDialog({ open, initial = "", onClose, onApply }) {
  const [text, setText] = useState(initial);
  const [mode, setMode] = useState("premium");
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState(null); // string | string[]
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setText(initial);
      setOutput(null);
      setError("");
    }
  }, [open, initial]);

  // Esc to close, Cmd/Ctrl+Enter to run
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose?.(); return; }
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        if (!loading && text.trim()) run();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, text, loading]);

  const run = async () => {
    setLoading(true);
    setError("");
    setOutput(null);
    try {
      const { data } = await api.post("/admin/ai/improve", { text, mode });
      if (mode === "variations") setOutput(data.variations || []);
      else setOutput(data.text || "");
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "AI error");
    } finally {
      setLoading(false);
    }
  };

  const variations = Array.isArray(output) ? output : [];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          data-testid="ai-dialog"
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={onClose} />
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-full max-w-2xl rounded-3xl border border-white/[0.08] bg-[#0c0c0e]/95 backdrop-blur-2xl shadow-[0_30px_120px_-20px_rgba(0,85,255,0.45)] overflow-hidden"
          >
            <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-[#0055FF]/22 blur-3xl" />
            <button
              onClick={onClose}
              className="absolute right-4 top-4 z-10 inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-zinc-300 hover:text-white hover:bg-white/[0.06] transition-colors"
              data-testid="ai-dialog-close"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="relative p-7 sm:p-9">
              <div className="flex items-center gap-2.5">
                <Sparkles className="h-4 w-4 text-[#00E5FF]" />
                <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">AI Assistant</div>
              </div>
              <h2 className="font-display mt-3 text-2xl sm:text-3xl font-semibold tracking-tight chrome-text">
                Improve with AI
              </h2>

              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={4}
                data-testid="ai-input"
                className="mt-6 w-full rounded-xl bg-white/[0.02] border border-white/[0.08] px-3.5 py-3 text-[14px] text-white placeholder:text-zinc-600 focus:outline-none focus:border-[#00E5FF]/40 focus:bg-white/[0.04] transition-colors resize-none"
                placeholder="Paste text to improve…"
              />
              <div className="mt-1 flex items-center justify-between text-[11px] text-zinc-600">
                <span>⌘⏎ to generate · Esc to close</span>
                <span className="tabular-nums">{text.length} ch</span>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {MODES.map((m) => (
                  <button
                    key={m.key}
                    type="button"
                    onClick={() => setMode(m.key)}
                    data-testid={`ai-mode-${m.key}`}
                    className={`px-3 py-1.5 rounded-full text-[12.5px] border transition-colors ${
                      mode === m.key
                        ? "border-[#00E5FF]/40 bg-white/[0.06] text-white"
                        : "border-white/[0.08] bg-white/[0.015] text-zinc-300 hover:bg-white/[0.04]"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              <div className="mt-5 flex items-center gap-3">
                <button
                  type="button"
                  onClick={run}
                  disabled={loading || !text.trim()}
                  data-testid="ai-run-btn"
                  className="inline-flex items-center gap-1.5 rounded-full bg-white text-black hover:bg-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors px-4 py-2 text-[13px] font-medium"
                >
                  {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                  {loading ? "Working…" : "Generate"}
                </button>
                {error && <span className="text-[12.5px] text-rose-400" data-testid="ai-error">{error}</span>}
              </div>

              {/* Output */}
              {output !== null && !loading && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="mt-7 space-y-3"
                  data-testid="ai-output"
                >
                  <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— Suggestion</div>
                  {mode === "variations" ? (
                    variations.map((v, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35, delay: 0.05 * i, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <SuggestionCard text={v} index={i + 1} onApply={() => onApply(v)} />
                      </motion.div>
                    ))
                  ) : (
                    <SuggestionCard text={output} onApply={() => onApply(output)} />
                  )}
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function SuggestionCard({ text, index, onApply }) {
  return (
    <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
      <div className="flex items-start gap-3">
        {index && (
          <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-zinc-500 mt-0.5">
            {String(index).padStart(2, "0")}
          </span>
        )}
        <p className="flex-1 text-[14px] text-white leading-relaxed whitespace-pre-line">{text}</p>
        <button
          type="button"
          onClick={onApply}
          data-testid="ai-apply-btn"
          className="shrink-0 inline-flex items-center gap-1 rounded-full bg-white text-black hover:bg-zinc-200 transition-colors px-3 py-1.5 text-[12px] font-medium"
        >
          <Check className="h-3 w-3" /> Apply
        </button>
      </div>
    </div>
  );
}
